import { useMemo, useState } from "react";
import { CreditCard, MessageSquareText, Minus, Plus } from "lucide-react";
import { addDays, parseISO } from "date-fns";
import { es, enUS } from "date-fns/locale";
import { Calendar } from "@/components/ui/calendar";
import { useLang } from "@/lib/i18n";
import { BLOCKS, PRICE_KEY, fmtUSD, toISO } from "@/lib/api";
import BookingDialog from "@/components/site/BookingDialog";
import type { BlockKey, Yacht } from "@/types";

interface Props { yacht: Yacht; blocked: string[]; initDate?: string; initGuests?: number; refresh: () => void }

export default function BookingPanel({ yacht, blocked, initDate, initGuests, refresh }: Props) {
  const { t, lang } = useLang();
  const blockedSet = useMemo(() => new Set(blocked), [blocked]);
  const [date, setDate] = useState<string>(initDate && !blockedSet.has(initDate) ? initDate : "");
  const [block, setBlock] = useState<BlockKey>("4h");
  const [guests, setGuests] = useState(Math.min(initGuests || 2, yacht.capacity));
  const [mode, setMode] = useState<"online" | "contact" | null>(null);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const amount = yacht.prices[PRICE_KEY[block]];
  const blockedDates = blocked.map((d) => parseISO(d));
  const nextDayBlocked = !!date && block === "overnight" && blockedSet.has(toISO(addDays(parseISO(date), 1)));
  const validDate = !!date && !blockedSet.has(date);
  const canBook = validDate && !nextDayBlocked;

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-[0_30px_80px_-40px_rgba(11,25,44,0.45)] overflow-hidden" data-testid="booking-panel">
      <div className="bg-[#0B192C] px-6 py-5">
        <p className="eyebrow !text-[#D4AF37]">{yacht.name}</p>
        <h3 className="mt-1 font-display text-2xl text-white">{t("book.title")}</h3>
      </div>
      <div className="p-5 sm:p-6 space-y-7">
        <section>
          <p className="text-sm font-semibold text-[#0B192C]">{t("book.date")}</p>
          <div className="mt-2 flex justify-center rounded-lg border border-slate-100">
            <Calendar mode="single" data-testid="availability-calendar" locale={lang === "es" ? es : enUS}
              selected={date ? parseISO(date) : undefined}
              onSelect={(d: Date | undefined) => setDate(d ? toISO(d) : "")}
              disabled={[{ before: today }, ...blockedDates]}
              modifiers={{ blocked: blockedDates }} modifiersClassNames={{ blocked: "day-blocked" }} />
          </div>
          <div className="mt-2 flex gap-5 text-xs text-slate-500">
            <span className="flex items-center gap-1.5"><i className="h-3 w-3 rounded-sm bg-red-100 border border-red-300" />{t("book.legendBlocked")}</span>
            <span className="flex items-center gap-1.5"><i className="h-3 w-3 rounded-sm bg-[#0B192C]" />{t("book.legendSelected")}</span>
          </div>
        </section>
        <section>
          <p className="text-sm font-semibold text-[#0B192C]">{t("book.block")}</p>
          <div className="mt-3 grid grid-cols-2 gap-2.5">
            {BLOCKS.map((b) => (
              <button key={b} type="button" onClick={() => setBlock(b)} data-testid={`block-option-${b}`}
                className={`rounded-lg border px-4 py-3 text-left transition-colors ${block === b ? "border-[#0B192C] bg-[#0B192C] text-white" : "border-slate-200 hover:border-[#00D2C8]"}`}>
                <span className={`block text-xs uppercase tracking-wider ${block === b ? "text-[#D4AF37]" : "text-slate-500"}`}>{t(`block.${b}`)}</span>
                <span className="mt-0.5 block font-display text-lg">{fmtUSD(yacht.prices[PRICE_KEY[b]])}</span>
              </button>
            ))}
          </div>
          {block === "overnight" && <p className="mt-2 text-xs text-slate-500">{t("book.overnightNote")}</p>}
        </section>
        <section className="flex items-center justify-between">
          <div>
            <p className="text-sm font-semibold text-[#0B192C]">{t("book.guests")}</p>
            <p className="text-xs text-slate-500">{t("book.max", { n: yacht.capacity })}</p>
          </div>
          <div className="flex items-center gap-4">
            <button type="button" data-testid="booking-guests-minus" onClick={() => setGuests((g) => Math.max(1, g - 1))} className="grid h-9 w-9 place-items-center rounded-full border border-slate-300 hover:border-[#00D2C8]"><Minus className="h-4 w-4" /></button>
            <span data-testid="booking-guests-value" className="font-display text-2xl w-8 text-center">{guests}</span>
            <button type="button" data-testid="booking-guests-plus" onClick={() => setGuests((g) => Math.min(yacht.capacity, g + 1))} className="grid h-9 w-9 place-items-center rounded-full border border-slate-300 hover:border-[#00D2C8]"><Plus className="h-4 w-4" /></button>
          </div>
        </section>
        <div className="flex items-end justify-between border-t border-slate-200 pt-5">
          <span className="text-sm text-slate-500">{t("book.total")}</span>
          <span data-testid="booking-total" className="font-display text-3xl text-[#0B192C]">{fmtUSD(amount)} <small className="text-xs text-slate-500 font-sans">USD</small></span>
        </div>
        {!validDate && <p className="text-sm text-[#B8922A]" data-testid="booking-select-date-hint">{t("book.selectDate")}</p>}
        {nextDayBlocked && <p className="text-sm text-red-600" data-testid="booking-next-day-blocked">{t("book.nextDayBlocked")}</p>}
        <div className="space-y-3">
          <button disabled={!canBook} onClick={() => setMode("online")} data-testid="yacht-book-whop-btn"
            className="w-full flex items-center gap-4 rounded-lg bg-[#D4AF37] px-5 py-4 text-left text-[#0B192C] transition-colors hover:bg-[#F3E5AB] disabled:opacity-40 disabled:cursor-not-allowed">
            <CreditCard className="h-5 w-5 shrink-0" />
            <span><b className="block text-sm">{t("book.payOnline")}</b><span className="block text-xs opacity-80">{t("book.payOnlineSub")}</span></span>
          </button>
          <button disabled={!canBook} onClick={() => setMode("contact")} data-testid="yacht-contact-request-btn"
            className="w-full flex items-center gap-4 rounded-lg border border-[#0B192C] px-5 py-4 text-left text-[#0B192C] transition-colors hover:bg-slate-50 disabled:opacity-40 disabled:cursor-not-allowed">
            <MessageSquareText className="h-5 w-5 shrink-0" />
            <span><b className="block text-sm">{t("book.contact")}</b><span className="block text-xs opacity-70">{t("book.contactSub")}</span></span>
          </button>
        </div>
      </div>
      {mode && (
        <BookingDialog open={!!mode} onOpenChange={(o) => !o && setMode(null)} mode={mode} yacht={yacht} date={date}
          block={block} guests={guests} amount={amount} onConflict={() => { setDate(""); refresh(); }} />
      )}
    </div>
  );
}
